import { Template, Font, checkTemplate, getInputFromTemplate, getDefaultFont } from '@pdfme/common';
import { Form, Viewer, Designer } from '@pdfme/ui';
import { generate } from '@pdfme/generator';
import { getPlugins } from './plugins';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function fromKebabCase(str: string): string {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const isJsonString = (str: string): boolean => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

// ============================================================================
// FONT CONFIGURATION
// ============================================================================

export const getFontsData = (): Font => ({
  ...getDefaultFont(),
  'PinyonScript-Regular': {
    fallback: false,
    data: 'https://fonts.gstatic.com/s/pinyonscript/v22/6xKpdSJbL9-e9LuoeQiDRQR8aOLQO4bhiDY.ttf',
  },
  NotoSerifJP: {
    fallback: false,
    data: 'https://fonts.gstatic.com/s/notoserifjp/v30/xn71YHs72GKoTvER4Gn3b5eMRtWGkp6o7MjQ2bwxOubAILO5wBCU.ttf',
  },
  NotoSansJP: {
    fallback: false,
    data: 'https://fonts.gstatic.com/s/notosansjp/v53/-F6jfjtqLzI2JPCgQBnw7HFyzSD-AsregP8VFBEj75vY0rw-oME.ttf',
  }
});

// ============================================================================
// FILE OPERATIONS
// ============================================================================

export const readFile = (file: File | null, type: 'text' | 'dataURL' | 'arrayBuffer'): Promise<string | ArrayBuffer> => {
  return new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.addEventListener('load', (e) => {
      if (e?.target?.result && file !== null) {
        resolve(e.target.result);
      }
    });
    
    if (file !== null) {
      switch (type) {
        case 'text':
          fileReader.readAsText(file);
          break;
        case 'dataURL':
          fileReader.readAsDataURL(file);
          break;
        case 'arrayBuffer':
          fileReader.readAsArrayBuffer(file);
          break;
      }
    }
  });
};

const getTemplateFromJsonFile = (file: File): Promise<Template> => {
  return readFile(file, 'text').then((jsonStr) => {
    const template: Template = JSON.parse(jsonStr as string);
    checkTemplate(template);
    return template;
  });
};

export const downloadJsonFile = (json: unknown, title: string): void => {
  if (typeof window !== 'undefined') {
    const blob = new Blob([JSON.stringify(json, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }
};

export const handleLoadTemplate = (
  e: React.ChangeEvent<HTMLInputElement>,
  currentRef: Designer | Form | Viewer | null
): void => {
  if (e.target?.files?.[0]) {
    getTemplateFromJsonFile(e.target.files[0])
      .then((template) => {
        if (currentRef) {
          currentRef.updateTemplate(template);
        }
      })
      .catch((error) => {
        alert(`Invalid template file.\n--------------------------\n${error}`);
      });
  }
};

// ============================================================================
// PDF GENERATION
// ============================================================================

export const generatePDF = async (currentRef: Designer | Form | Viewer | null): Promise<void> => {
  if (!currentRef) return;
  
  const template = currentRef.getTemplate();
  const options = currentRef.getOptions();
  const inputs = typeof (currentRef as Viewer | Form).getInputs === 'function'
    ? (currentRef as Viewer | Form).getInputs()
    : getInputFromTemplate(template);
  const font = getFontsData();

  try {
    const pdf = await generate({
      template,
      inputs,
      options: {
        font,
        lang: options.lang,
        title: 'pdfme-document',
      },
      plugins: getPlugins(),
    });

    const blob = new Blob([pdf.buffer], { type: 'application/pdf' });
    window.open(URL.createObjectURL(blob));
  } catch (error) {
    alert(`${error}\n\nCheck the console for full stack trace`);
    throw error;
  }
};

// ============================================================================
// TEMPLATE FETCHING
// ============================================================================

export const getTemplateById = async (templateId: string): Promise<Template> => {
  const template = await fetch(`/template-assets/${templateId}/template.json`)
    .then((res) => res.json());
  checkTemplate(template);
  return template as Template;
};

// ============================================================================
// BASIC TEMPLATES
// ============================================================================

export const getBlankTemplate = (): Template => ({
  schemas: [[]],
  basePdf: {
    width: 210,
    height: 297,
    padding: [20, 10, 20, 10],
  },
});

export const getMultiPageBlankTemplate = (pageCount: number = 3): Template => ({
  schemas: Array.from({ length: pageCount }, () => []),
  basePdf: {
    width: 210,
    height: 297,
    padding: [20, 10, 20, 10],
  },
});

// ============================================================================
// SAMPLE MULTI-PAGE TEMPLATES
// ============================================================================

export const createSampleMultiPageTemplate = (): Template => ({
  schemas: [
    // Page 1 - Cover Page
    [
      {
        name: "cover_title",
        type: "text",
        position: { x: 20, y: 50 },
        width: 170,
        height: 25,
        fontSize: 24,
        fontColor: "#000000",
        content: "Multi-Page Document"
      },
      {
        name: "cover_subtitle",
        type: "text", 
        position: { x: 20, y: 80 },
        width: 170,
        height: 20,
        fontSize: 16,
        fontColor: "#666666",
        content: "Sample Template with Multiple Pages"
      },
      {
        name: "cover_author",
        type: "text",
        position: { x: 20, y: 200 },
        width: 170,
        height: 15,
        fontSize: 12,
        content: "Created with PDFme Designer"
      },
      {
        name: "cover_date",
        type: "text",
        position: { x: 20, y: 220 },
        width: 170,
        height: 15,
        fontSize: 10,
        fontColor: "#999999",
        content: new Date().toLocaleDateString()
      },
      {
        name: "cover_logo_placeholder",
        type: "text",
        position: { x: 20, y: 120 },
        width: 170,
        height: 60,
        fontSize: 12,
        backgroundColor: "#f0f0f0",
        content: "Logo/Image Placeholder\n(You can replace this with an image field)"
      }
    ],
    // Page 2 - Content Page
    [
      {
        name: "page2_header",
        type: "text",
        position: { x: 20, y: 20 },
        width: 170,
        height: 15,
        fontSize: 10,
        fontColor: "#888888",
        content: "Page 2 - Main Content"
      },
      {
        name: "chapter1_title",
        type: "text",
        position: { x: 20, y: 40 },
        width: 170,
        height: 20,
        fontSize: 18,
        fontColor: "#000000",
        content: "Chapter 1: Introduction"
      },
      {
        name: "chapter1_content",
        type: "text",
        position: { x: 20, y: 70 },
        width: 170,
        height: 150,
        fontSize: 12,
        content: "This is the main content of the document. You can edit this text and add more fields as needed.\n\nYou can create multiple paragraphs, add formatting, and include various types of content on each page.\n\nEach page can have different layouts and field types including:\n• Text fields\n• Image fields\n• Signature fields\n• Date fields\n• And more..."
      },
      {
        name: "page2_footer",
        type: "text",
        position: { x: 20, y: 260 },
        width: 170,
        height: 10,
        fontSize: 8,
        fontColor: "#888888",
        content: "Page 2 of 3"
      }
    ],
    // Page 3 - Summary Page
    [
      {
        name: "page3_header",
        type: "text",
        position: { x: 20, y: 20 },
        width: 170,
        height: 15,
        fontSize: 10,
        fontColor: "#888888",
        content: "Page 3 - Summary"
      },
      {
        name: "summary_title",
        type: "text",
        position: { x: 20, y: 40 },
        width: 170,
        height: 20,
        fontSize: 18,
        fontColor: "#000000",
        content: "Summary & Conclusion"
      },
      {
        name: "summary_content",
        type: "text",
        position: { x: 20, y: 70 },
        width: 170,
        height: 100,
        fontSize: 12,
        content: "This page summarizes the key points covered in the document.\n\nKey takeaways:\n• Multi-page templates are easy to create\n• Each page can have unique content and layout\n• You can add, remove, and duplicate pages as needed"
      },
      {
        name: "contact_info",
        type: "text",
        position: { x: 20, y: 180 },
        width: 170,
        height: 40,
        fontSize: 10,
        backgroundColor: "#f9f9f9",
        content: "Contact Information:\nEmail: example@domain.com\nPhone: (555) 123-4567\nWebsite: www.example.com"
      },
      {
        name: "signature_field",
        type: "text",
        position: { x: 20, y: 230 },
        width: 80,
        height: 20,
        fontSize: 10,
        content: "Signature: ________________"
      },
      {
        name: "date_field",
        type: "text",
        position: { x: 110, y: 230 },
        width: 80,
        height: 20,
        fontSize: 10,
        content: "Date: ________________"
      },
      {
        name: "page3_footer",
        type: "text",
        position: { x: 20, y: 260 },
        width: 170,
        height: 10,
        fontSize: 8,
        fontColor: "#888888",
        content: "Page 3 of 3 - End of Document"
      }
    ]
  ],
  basePdf: {
    width: 210,
    height: 297,
    padding: [20, 10, 20, 10],
  },
});

export const createBusinessCardTemplate = (): Template => ({
  schemas: [
    [
      // First business card
      {
        name: "card1_name",
        type: "text",
        position: { x: 20, y: 30 },
        width: 80,
        height: 10,
        fontSize: 14,
        fontColor: "#000000",
        content: "John Doe"
      },
      {
        name: "card1_title",
        type: "text",
        position: { x: 20, y: 45 },
        width: 80,
        height: 8,
        fontSize: 10,
        fontColor: "#666666",
        content: "Software Engineer"
      },
      {
        name: "card1_contact",
        type: "text",
        position: { x: 20, y: 55 },
        width: 80,
        height: 15,
        fontSize: 8,
        content: "john@example.com\n(555) 123-4567"
      },
      // Second business card
      {
        name: "card2_name",
        type: "text",
        position: { x: 110, y: 30 },
        width: 80,
        height: 10,
        fontSize: 14,
        fontColor: "#000000",
        content: "Jane Smith"
      },
      {
        name: "card2_title",
        type: "text",
        position: { x: 110, y: 45 },
        width: 80,
        height: 8,
        fontSize: 10,
        fontColor: "#666666",
        content: "Product Manager"
      },
      {
        name: "card2_contact",
        type: "text",
        position: { x: 110, y: 55 },
        width: 80,
        height: 15,
        fontSize: 8,
        content: "jane@example.com\n(555) 987-6543"
      }
    ]
  ],
  basePdf: {
    width: 210,
    height: 297,
    padding: [10, 10, 10, 10],
  },
});

export const createInvoiceTemplate = (): Template => ({
  schemas: [
    // Page 1 - Invoice Header and Items
    [
      {
        name: "invoice_header",
        type: "text",
        position: { x: 20, y: 20 },
        width: 170,
        height: 25,
        fontSize: 20,
        fontColor: "#000000",
        content: "INVOICE"
      },
      {
        name: "invoice_number",
        type: "text",
        position: { x: 20, y: 50 },
        width: 80,
        height: 15,
        fontSize: 12,
        content: "Invoice #: INV-001"
      },
      {
        name: "invoice_date",
        type: "text",
        position: { x: 110, y: 50 },
        width: 80,
        height: 15,
        fontSize: 12,
        content: "Date: " + new Date().toLocaleDateString()
      },
      {
        name: "bill_to",
        type: "text",
        position: { x: 20, y: 80 },
        width: 80,
        height: 40,
        fontSize: 10,
        content: "Bill To:\nClient Name\nClient Address\nCity, State ZIP"
      },
      {
        name: "items_header",
        type: "text",
        position: { x: 20, y: 130 },
        width: 170,
        height: 15,
        fontSize: 12,
        backgroundColor: "#f0f0f0",
        content: "Description | Qty | Rate | Amount"
      },
      {
        name: "items_list",
        type: "text",
        position: { x: 20, y: 150 },
        width: 170,
        height: 80,
        fontSize: 10,
        content: "Item 1 - Description | 2 | $50.00 | $100.00\nItem 2 - Description | 1 | $75.00 | $75.00\nItem 3 - Description | 3 | $25.00 | $75.00"
      }
    ],
    // Page 2 - Additional Items and Totals
    [
      {
        name: "continued_header",
        type: "text",
        position: { x: 20, y: 20 },
        width: 170,
        height: 15,
        fontSize: 12,
        content: "Invoice INV-001 (Continued)"
      },
      {
        name: "additional_items",
        type: "text",
        position: { x: 20, y: 40 },
        width: 170,
        height: 60,
        fontSize: 10,
        content: "Item 4 - Description | 1 | $100.00 | $100.00\nItem 5 - Description | 2 | $30.00 | $60.00"
      },
      {
        name: "totals",
        type: "text",
        position: { x: 110, y: 120 },
        width: 80,
        height: 40,
        fontSize: 12,
        content: "Subtotal: $410.00\nTax (8%): $32.80\nTotal: $442.80"
      },
      {
        name: "payment_terms",
        type: "text",
        position: { x: 20, y: 180 },
        width: 170,
        height: 30,
        fontSize: 10,
        backgroundColor: "#f9f9f9",
        content: "Payment Terms: Net 30 days\nThank you for your business!"
      }
    ]
  ],
  basePdf: {
    width: 210,
    height: 297,
    padding: [20, 10, 20, 10],
  },
});

export const createReportTemplate = (): Template => ({
  schemas: [
    // Page 1 - Title Page
    [
      {
        name: "report_title",
        type: "text",
        position: { x: 20, y: 80 },
        width: 170,
        height: 30,
        fontSize: 24,
        fontColor: "#000000",
        content: "Annual Report 2024"
      },
      {
        name: "report_subtitle",
        type: "text",
        position: { x: 20, y: 120 },
        width: 170,
        height: 20,
        fontSize: 16,
        fontColor: "#666666",
        content: "Company Performance Overview"
      },
      {
        name: "company_info",
        type: "text",
        position: { x: 20, y: 180 },
        width: 170,
        height: 30,
        fontSize: 12,
        content: "Your Company Name\nPrepared by: [Author Name]\nDate: " + new Date().toLocaleDateString()
      }
    ],
    // Page 2 - Executive Summary
    [
      {
        name: "exec_summary_title",
        type: "text",
        position: { x: 20, y: 30 },
        width: 170,
        height: 20,
        fontSize: 18,
        content: "Executive Summary"
      },
      {
        name: "exec_summary_content",
        type: "text",
        position: { x: 20, y: 60 },
        width: 170,
        height: 180,
        fontSize: 12,
        content: "This report provides a comprehensive overview of our company's performance in 2024.\n\nKey Highlights:\n• Revenue increased by 25%\n• Customer satisfaction improved to 94%\n• Expanded to 3 new markets\n• Launched 5 new product lines\n\nChallenges:\n• Supply chain disruptions\n• Increased competition\n• Regulatory changes\n\nLooking ahead to 2025, we expect continued growth and plan to invest in new technologies and market expansion."
      }
    ],
    // Page 3 - Financial Data
    [
      {
        name: "financial_title",
        type: "text",
        position: { x: 20, y: 30 },
        width: 170,
        height: 20,
        fontSize: 18,
        content: "Financial Performance"
      },
      {
        name: "financial_data",
        type: "text",
        position: { x: 20, y: 60 },
        width: 170,
        height: 100,
        fontSize: 12,
        backgroundColor: "#f8f9fa",
        content: "Revenue: $2,500,000 (+25% YoY)\nExpenses: $1,800,000 (+15% YoY)\nNet Profit: $700,000 (+50% YoY)\nCash Flow: $450,000 (+30% YoY)"
      },
      {
        name: "chart_placeholder",
        type: "text",
        position: { x: 20, y: 170 },
        width: 170,
        height: 60,
        fontSize: 10,
        backgroundColor: "#e9ecef",
        content: "[Financial Chart Placeholder]\n\nYou can replace this with an image field to include actual charts and graphs."
      }
    ]
  ],
  basePdf: {
    width: 210,
    height: 297,
    padding: [20, 10, 20, 10],
  },
});

// ============================================================================
// TEMPLATE FACTORY
// ============================================================================

export type TemplateType = 'blank' | 'sample' | 'business-card' | 'invoice' | 'report';

export const createCustomMultiPageTemplate = (
  pageCount: number = 3, 
  templateType: TemplateType = 'blank'
): Template => {
  switch (templateType) {
    case 'sample':
      return createSampleMultiPageTemplate();
    case 'business-card':
      return createBusinessCardTemplate();
    case 'invoice':
      return createInvoiceTemplate();
    case 'report':
      return createReportTemplate();
    case 'blank':
    default:
      return getMultiPageBlankTemplate(pageCount);
  }
};

// ============================================================================
// INTERNATIONALIZATION
// ============================================================================

export const translations: { label: string; value: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ko', label: 'Korean' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ar', label: 'Arabic' },
  { value: 'th', label: 'Thai' },
  { value: 'pl', label: 'Polish' },
  { value: 'it', label: 'Italian' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
];